<template>
  <div class="upcoming-awards-table">
    <div class="upcoming-awards-table__filters">
      <b-form-select
        v-if="hasDivisions"
        v-model="selectedDivision"
        :options="divisionOptions"
        class="upcoming-awards-table__filter"
      />
      <b-form-select v-model="selectedEventType" :options="eventTypeOptions" class="upcoming-awards-table__filter" />
      <b-form-select
        v-model="selectedRange"
        :options="UPCOMING_AWARDS_DATE_RANGES"
        class="upcoming-awards-table__filter"
      />
      <b-form-datepicker
        v-if="selectedRange === 'custom'"
        v-model="customDate"
        :min="todayIso"
        placeholder="Select a date"
        class="upcoming-awards-table__datepicker"
      />
      <b-button v-if="hasActiveFilters" variant="link" class="upcoming-awards-table__reset" @click="resetFilters">
        <font-awesome-icon :icon="['fal', 'times']" class="mr-1" />
        Reset filters
      </b-button>
      <div class="upcoming-awards-table__append">
        <export-button
          file-name="Recognition_Upcoming_Awards"
          :export-headers="exportHeaders"
          :export-data="filteredAwards"
          :export-file-rows="exportFileRow"
          :disabled="filteredAwards.length === 0"
          :enable-pdf="false"
          display-text="Export as"
        />
      </div>
    </div>

    <app-loading :loading="loading" />
    <template v-if="!loading">
      <b-table
        v-if="filteredAwards.length > 0"
        :items="filteredAwards"
        :fields="UPCOMING_AWARDS_FIELDS"
        hover
        responsive
        borderless
        show-empty
        class="upcoming-awards-table__table"
      >
        <template #cell(full_name)="{ item }">
          <div class="upcoming-awards-table__employee">
            <div class="upcoming-awards-table__avatar">
              <span class="upcoming-awards-table__initials">{{ getInitials(item.full_name) }}</span>
            </div>
            <span class="upcoming-awards-table__name">{{ item.full_name }}</span>
          </div>
        </template>

        <template #cell(division)="{ item }">
          <span v-if="item.employeeDivisionNames.length > 0">{{ item.employeeDivisionNames.join(', ') }}</span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(work_email)="{ item }">
          <span class="upcoming-awards-table__email">{{ item.work_email }}</span>
        </template>

        <template #cell(milestone)="{ item }">
          <span>{{ formatMilestone(item) }}</span>
        </template>

        <template #cell(scheduled_date)="{ item }">
          <span>{{ formatDate(item.scheduled_date) }}</span>
        </template>

        <template #cell(amount_minor)="{ item }">
          <span v-if="item.amount_minor">{{ formatCurrency(item.amount_minor) }}</span>
          <span v-else class="text-muted">--</span>
        </template>
      </b-table>

      <div v-else class="upcoming-awards-table__empty">
        <p class="text-muted">No upcoming awards {{ windowPhrase }}.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import moment from 'moment';
import AppLoading from '@/components/AppLoading.vue';
import ExportButton from '@/components/ExportButton.vue';
import { toCurrency, getInitials } from '@/utils';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import State from '@/state/State';
import {
  UPCOMING_AWARDS_DATE_RANGES,
  UPCOMING_AWARDS_DEFAULT_RANGE,
  RECOGNITION_MILESTONE_EVENT_OPTIONS,
  RECOGNITION_MILESTONE_EVENT_TYPES,
  getDateToForRange,
  getUpcomingWindowPhrase,
  toIsoDateString,
} from '@/pages/recognition/constants';

const props = defineProps({
  awards: {
    type: Array,
    default: () => [],
  },
  programs: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['change', 'update:summary', 'update:window']);

const { divisions, hasDivisions } = useDivisionSelection();

const selectedRange = ref(UPCOMING_AWARDS_DEFAULT_RANGE);
const selectedDivision = ref(null);
const selectedEventType = ref(null);
const customDate = ref(null);
const todayIso = toIsoDateString(new Date());

const resolvedDateTo = computed(() =>
  selectedRange.value === 'custom' ? customDate.value || null : getDateToForRange(selectedRange.value),
);

watch(resolvedDateTo, dateTo => {
  if (dateTo) {
    emit('change', dateTo);
  }
});

const windowPhrase = computed(() => getUpcomingWindowPhrase(selectedRange.value, customDate.value));

// The page's funds-shortfall banner names this window too, but it can't read it off the summary
// below (that goes null once the filters empty the table, while the shortfall doesn't). Report the
// selection separately, and only once it resolves to a date — picking "Custom date" before picking
// a day refetches nothing, so the banner must keep describing the awards it is actually counting.
watch([selectedRange, customDate], () => {
  if (resolvedDateTo.value) {
    emit('update:window', { range: selectedRange.value, customDate: customDate.value });
  }
});

const UPCOMING_AWARDS_FIELDS = [
  { key: 'full_name', label: 'Name' },
  { key: 'division', label: 'Division' },
  { key: 'work_email', label: 'Email' },
  { key: 'milestone', label: 'Milestone' },
  { key: 'scheduled_date', label: 'Scheduled' },
  { key: 'amount_minor', label: 'Value' },
];

const eventTypeOptions = [{ value: null, text: 'All award types' }, ...RECOGNITION_MILESTONE_EVENT_OPTIONS];

const currency = computed(() => State.state?.company?.default_currency);

const divisionOptions = computed(() => {
  const options = [{ value: null, text: 'Entire company' }];
  for (const division of divisions.value) {
    options.push({ value: division.division_id, text: division.name });
  }
  return options;
});

const divisionMap = computed(() => {
  const map = {};
  for (const division of divisions.value) {
    map[division.division_id] = division.name;
  }
  return map;
});

const anniversaryYearByProgram = computed(() => {
  const map = {};
  for (const program of props.programs) {
    const year = program.properties?.milestone?.anniversary_year;
    if (year) {
      map[program.recognition_award_program_id] = Number(year);
    }
  }
  return map;
});

const enrichedAwards = computed(() =>
  props.awards.map(award => {
    const employeeDivisionIds = award.employee_division_ids || [];
    const employeeDivisionNames = employeeDivisionIds.map(divId => divisionMap.value[divId]).filter(name => name);
    return { ...award, employeeDivisionNames };
  }),
);

const filteredAwards = computed(() =>
  enrichedAwards.value.filter(award => {
    if (selectedEventType.value && award.event_type !== selectedEventType.value) {
      return false;
    }
    if (selectedDivision.value && !(award.employee_division_ids || []).includes(selectedDivision.value)) {
      return false;
    }
    return true;
  }),
);

const totalValueMinor = computed(() => filteredAwards.value.reduce((sum, award) => sum + (award.amount_minor || 0), 0));

const formattedTotalValue = computed(() => toCurrency(totalValueMinor.value, currency.value));

const summary = computed(() =>
  filteredAwards.value.length > 0 ? { total: formattedTotalValue.value, windowText: windowPhrase.value } : null,
);

watch(summary, value => emit('update:summary', value), { immediate: true });

const hasActiveFilters = computed(
  () =>
    selectedDivision.value !== null ||
    selectedEventType.value !== null ||
    selectedRange.value !== UPCOMING_AWARDS_DEFAULT_RANGE,
);

const resetFilters = () => {
  selectedDivision.value = null;
  selectedEventType.value = null;
  selectedRange.value = UPCOMING_AWARDS_DEFAULT_RANGE;
  customDate.value = null;
};

const formatCurrency = value => toCurrency(value, currency.value);

const formatDate = dateStr => {
  if (!dateStr) {
    return '-';
  }
  return moment(dateStr).format('DD MMM YYYY');
};

const formatMilestone = item => {
  if (item.event_type === RECOGNITION_MILESTONE_EVENT_TYPES.BIRTHDAY) {
    return 'Birthday';
  }
  if (item.event_type === RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY) {
    const years = anniversaryYearByProgram.value[item.recognition_award_program_id];
    if (years) {
      return `Anniversary (${years} ${years === 1 ? 'Year' : 'Years'})`;
    }
    return 'Anniversary';
  }
  return item.event_type || '-';
};

const exportHeaders = ['Name', 'Division', 'Email', 'Milestone', 'Scheduled', 'Value'];

const exportFileRow = (item, format) => {
  const isDataExport = format === 'csv' || format === 'xlsx';
  let value = '';
  if (item.amount_minor) {
    value = isDataExport ? (item.amount_minor / 100).toFixed(2) : formatCurrency(item.amount_minor);
  }
  const division = item.employeeDivisionNames.length > 0 ? item.employeeDivisionNames.join(', ') : '-';
  return [
    item.full_name || '-',
    division,
    item.work_email || '-',
    formatMilestone(item),
    formatDate(item.scheduled_date),
    value,
  ];
};
</script>

<style scoped lang="scss">
.upcoming-awards-table {
  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    margin-bottom: 1rem;
    background: var(--palette-color-default-lighten-95);
    border-radius: 0.5rem;
  }

  &__filter {
    width: auto;
    min-width: 170px;
    border-color: var(--palette-color-default-lighten-80);
    padding: 0.5rem 2.25rem 0.5rem 1rem;
    text-overflow: ellipsis;

    &:focus {
      border-color: var(--palette-color-primary);
      box-shadow: none;
    }
  }

  &__datepicker {
    width: auto;
    min-width: 200px;
  }

  &__reset {
    color: var(--palette-color-default);
    text-decoration: none;
    padding: 0.5rem 1rem;

    &:hover {
      text-decoration: none;
      color: var(--palette-color-default-lighten-20);
    }
  }

  &__append {
    margin-left: auto;

    :deep(.export-button.btn-group) {
      margin-left: 0;
      border-radius: 8px;

      .btn:not([class*='btn-outline-']):not(.btn-icon):not(.btn-light) {
        border: 1px solid var(--palette-color-default-lighten-80);
        border-radius: 8px;
        padding: 0.5rem 1rem;
        font-size: 0.875rem;
        background: var(--palette-color-base-white);
        color: var(--palette-color-default);
        height: 100%;

        &:focus {
          border-color: var(--palette-color-primary);
          box-shadow: none;
        }
      }
    }
  }

  &__table {
    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 0.6rem;
    }

    :deep(tbody td) {
      padding: 1.25rem 0.6rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__employee {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    background: var(--palette-color-default-lighten-90);
    flex-shrink: 0;
  }

  &__initials {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--palette-color-default-lighten-30);
  }

  &__name {
    font-weight: 500;
  }

  &__email {
    color: var(--palette-color-default-lighten-40);
  }

  &__empty {
    padding: 3rem;
    text-align: center;
  }
}
</style>
